import { useState, useEffect } from "react";
import { Search, ChevronUp, Clock, AlertTriangle, BookOpen, CheckCircle2, Info, Users, ClipboardCheck, Utensils, Gamepad2, MessageSquare, FileText, Heart, Bell, HelpCircle, ArrowRight, Waves, Bus, Star, Clipboard, Flag, Trophy, GraduationCap, MapPin, X, Printer, Download, CalendarDays } from "lucide-react";
import { Badge } from "../components/ui/badge";
import SopSwitcher from "./SopSwitcher";
import "../styles/tutorialPrint.css";

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = {
  login: `${API}/api/assets/tutorial/01_login.png`,
  homescreen: `${API}/api/assets/tutorial/02_homescreen.png`,
  attendance: `${API}/api/assets/tutorial/03_attendance.png`,
  buddy: `${API}/api/assets/tutorial/04_buddy.png`,
  messages: `${API}/api/assets/tutorial/05_messages.png`,
  incidents: `${API}/api/assets/tutorial/06_incidents.png`,
  alerts: `${API}/api/assets/tutorial/07_alerts.png`,
  roster: `${API}/api/assets/tutorial/08_roster.png`,
  campers: `${API}/api/assets/tutorial/09_campers.png`,
};

const SECTIONS = [
  { id: "logging-in", num: "1", title: "Logging In" },
  { id: "home-screen", num: "2", title: "Your Home Screen" },
  { id: "daily-roster", num: "3", title: "Daily Roster" },
  { id: "camper-card", num: "4", title: "The Camper Card" },
  { id: "group-attendance", num: "5", title: "Group Attendance (do this FIRST)", deadline: true },
  { id: "attendance", num: "6", title: "Conflict Match (check AFTER Group Attendance)", deadline: true },
  { id: "buddy-sheet", num: "7", title: "Buddy Check Sheet (Pool Only)" },
  { id: "dining", num: "8", title: "Lunch Ordering", deadline: true },
  { id: "alerts", num: "9", title: "Alerts — Early Dismissals & Late Arrivals" },
  { id: "messages", num: "10", title: "Messages" },
  { id: "incident-reports", num: "11", title: "Incident Reports" },
  { id: "evaluations", num: "12", title: "End-of-Season Evaluations" },
  { id: "trip-form", num: "13", title: "Trip Accountability Form" },
  { id: "group-schedule", num: "14", title: "Group Schedule" },
  { id: "health-notes", num: "15", title: "Health Notes" },
  { id: "bus-info", num: "16", title: "Bus Information" },
  { id: "hobbies", num: "17", title: "Hobbies, Intramurals & Intensives" },
  { id: "color-war", num: "18", title: "Color War" },
  { id: "troubleshooting", num: "19", title: "Troubleshooting" },
];

function Deadline() {
  return (
    <div className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 my-3 print:bg-red-100 print:text-red-900">
      <Clock className="w-4 h-4 flex-shrink-0" />
      <span>DEADLINE: Buddy Sheets by 9:20 AM / Bus by 9:30 AM / Lunch by 9:30 AM / Group Attendance by 10:00 AM</span>
    </div>
  );
}

function Why({ children }) {
  return (
    <div className="sop-why sop-block bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-blue-900 mb-1 flex items-center gap-1"><Info className="w-4 h-4" /> Why this matters</p>
      <p className="text-sm text-blue-800">{children}</p>
    </div>
  );
}

function Mistakes({ items }) {
  return (
    <div className="sop-mistakes sop-block bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Common mistakes to avoid</p>
      <ul className="list-disc pl-5 space-y-1">
        {items.map((item, i) => <li key={i} className="text-sm text-amber-800">{item}</li>)}
      </ul>
    </div>
  );
}

function Img({ caption, src }) {
  if (!src) return null;
  return (
    <div className="sop-img sop-block my-4 rounded-xl overflow-hidden border shadow-sm">
      <img src={src} alt={caption} className="w-full object-contain max-h-[500px] bg-gray-50" loading="lazy" />
      <p className="text-xs text-gray-500 italic px-3 py-2 bg-gray-50 border-t">{caption}</p>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="sop-steps sop-block list-decimal pl-6 space-y-2 my-3">
      {items.map((item, i) => <li key={i} className="text-base text-gray-700 leading-relaxed">{item}</li>)}
    </ol>
  );
}

function H({ id, num, title, deadline, icon: Icon }) {
  return (
    <div id={id} className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6 text-sky-700 flex-shrink-0" />}
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{num}. {title}</h2>
      </div>
      {deadline && <Deadline />}
    </div>
  );
}

export default function TutorialCounselor() {
  const [search, setSearch] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-sky-900 text-white shadow-lg print:static print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-sky-200">Counselor Tutorial</p>
            </div>
            <div className="hidden sm:block">
              <SopSwitcher current="/tutorialcounselor" accent="sky" />
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <div className="sm:hidden">
              <SopSwitcher current="/tutorialcounselor" accent="sky" />
            </div>
            <a
              href={`${API}/api/tutorial/pdf/counselor`}
              className="text-xs bg-white text-sky-900 hover:bg-sky-50 px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1"
              data-testid="download-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
            <button
              onClick={() => window.print()}
              className="text-xs bg-sky-700 hover:bg-sky-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1"
              data-testid="print-btn"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:flex lg:gap-8">
        {/* TOC */}
        <nav className="lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0 print:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-full text-left bg-gray-50 border rounded-xl p-3 text-sm font-bold text-gray-700 flex items-center justify-between">
            Table of Contents <ChevronUp className={`w-4 h-4 transition-transform ${menuOpen ? "" : "rotate-180"}`} />
          </button>
          <div className={`lg:sticky lg:top-20 ${menuOpen ? "block" : "hidden lg:block"} mt-2 lg:mt-0`}>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filtered.map(s => (
                <li key={s.id}><a href={`#${s.id}`} onClick={() => setMenuOpen(false)} className={`block px-3 py-1.5 text-sm rounded-lg hover:bg-sky-50 ${s.deadline ? "font-bold text-red-700" : "text-gray-600"}`}>{s.num}. {s.title}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {/* INTRO */}
          <section className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">What is Camper Snapshot?</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">Camper Snapshot is Rolling River Day Camp's all-in-one camper operations platform. It is the single daily source of truth for attendance, pool buddy checks, incident reports, lunch ordering, hobbies and intramural selections, messaging, pickup notifications, and camper-specific notes.</p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">The system tracks attendance in real time, filters rosters by session type (Full Season, HS1, HS2, 6-Week, Flex 20/25/30), manages division-specific programming (hobbies, intramurals, intensives), handles lunch ordering, delivers automated pickup notifications, supports in-app messaging to parents (email and SMS), generates PDF and XLSX reports, and houses pool buddy check sheets and incident reports.</p>

            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-red-900 flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5" /> CRITICAL DAILY DEADLINES</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3 bg-red-100 rounded-lg p-3"><Waves className="w-5 h-5 text-red-700" /><span className="font-bold text-red-900">Buddy Sheets:</span><span className="text-red-800">Must be submitted by <strong>9:20 AM</strong></span></div>
                <div className="flex items-center gap-3 bg-red-100 rounded-lg p-3"><ClipboardCheck className="w-5 h-5 text-red-700" /><span className="font-bold text-red-900">Bus Attendance:</span><span className="text-red-800">Must be submitted by <strong>9:30 AM</strong></span></div>
                <div className="flex items-center gap-3 bg-red-100 rounded-lg p-3"><ClipboardCheck className="w-5 h-5 text-red-700" /><span className="font-bold text-red-900">Group Attendance:</span><span className="text-red-800">Must be submitted by <strong>10:00 AM</strong></span></div>
                <div className="flex items-center gap-3 bg-red-100 rounded-lg p-3"><Utensils className="w-5 h-5 text-red-700" /><span className="font-bold text-red-900">Lunch Orders:</span><span className="text-red-800">Must be submitted by <strong>9:30 AM</strong></span></div>
              </div>
            </div>

            <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-sky-900 flex items-center gap-2 mb-2"><HelpCircle className="w-5 h-5" /> Tech Support</h3>
              <p className="text-base text-sky-800">If something isn't working or you need help, contact <strong>Brian Stein</strong>.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-5 border mb-4">
              <h3 className="text-sm font-bold text-gray-700 mb-2">Camp Terminology</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                <div><strong>Skippers</strong> — Nursery through Kindergarten (group codes start with N)</div>
                <div><strong>Mariners</strong> — 1st through 4th grade (Boys & Girls)</div>
                <div><strong>Navigators</strong> — 5th through 8th grade (Boys & Girls)</div>
                <div><strong>CIT</strong> — Counselors in Training, ages 15–16</div>
                <div><strong>HS1 / HS2</strong> — Half Season 1 or 2</div>
                <div><strong>Flex 20/25/30</strong> — Flexible attendance packages</div>
                <div><strong>Intensives</strong> — Navigator-only multi-week deep-dive activities</div>
                <div><strong>Intramurals</strong> — Division sports competitions</div>
                <div><strong>Swim Color</strong> — Wristband color indicating swim level</div>
                <div><strong>Color War</strong> — Camp-wide Blue vs White team competition</div>
              </div>
            </div>
            <p className="text-xs text-gray-400">Last updated: February 2026 — Roster overlays (AM/PM exception red box), persistent late/dismissed amber banners, attendance-conflict-match supervisor auto-SMS, buddy sheet admin lock.</p>
          </section>

          {/* ═══ 1. LOGGING IN ═══ */}
          <H id="logging-in" num="1" title="Logging In" icon={Users} />
          <div className="mt-4 space-y-3">
            <Steps items={[
              "Open your browser and go to campersnapshot.com.",
              "You will see the login page. There are tabs for Counselor, Supervisor, Admin, and Athletics.",
              "Make sure the Counselor tab is selected.",
              "Enter your Group Code (e.g., B01, G12, N04). Any format works — b1, B1, b01, B01.",
              "Enter your Password — this is your group name (for example, B01's password is \"Lobsters\"). Case-sensitive.",
              "Tap \"Access My Group\" to log in.",
              "If login fails, double-check spelling and capitalization. Contact your supervisor or Brian Stein for help.",
            ]} />
            <Img caption="Login screen — enter Group Code and Password, then tap Access My Group" src={IMG.login} />
            <Why>You must be logged in early enough to complete every morning deadline (Buddy Sheets 9:20 AM, Bus 9:30 AM, Lunch 9:30 AM, Group Attendance 10:00 AM).</Why>
            <Mistakes items={["Wrong capitalization in the password — passwords are case-sensitive", "Trying to log in with a personal email — use the group code"]} />
          </div>

          {/* ═══ 2. HOME SCREEN ═══ */}
          <H id="home-screen" num="2" title="Your Home Screen" icon={BookOpen} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">After logging in, you see your group's home screen. At the top is your group name (e.g., "B01 Lobsters") and the current camp year. Below that are <strong>card buttons</strong> — each one opens a different tool. The cards you see depend on your group's division.</p>
            <Img caption="Home screen — each card opens a different feature. Tap any card to enter." src={IMG.homescreen} />
            <p className="text-base text-gray-700 font-medium">Here's what each card does:</p>
            <div className="space-y-2">
              {[
                { icon: Heart, name: "Buddy Sheet", desc: "Pool buddy check — drag and drop campers into swim-color-matched pairs for water activities." },
                { icon: Users, name: "Daily Roster", desc: "See every camper scheduled to be at camp TODAY. Filters by session type automatically." },
                { icon: Users, name: "Campers", desc: "Full list of all campers in your group with detailed profiles — medical, allergies, parent contacts, swim level, bus info." },
                { icon: MapPin, name: "Trip Form", desc: "Trip accountability form — headcount sheet for field trips and off-site activities." },
                { icon: Star, name: "Evaluations", desc: "End-of-season camper evaluations with AI-assisted summaries." },
                { icon: MessageSquare, name: "Messages", desc: "Send emails and/or texts to parents. View conversation threads and replies." },
                { icon: AlertTriangle, name: "Incident Report", desc: "Log any injury, behavioral issue, conflict, or safety concern. Supervisor notified automatically." },
                { icon: ClipboardCheck, name: "Conflict Match", desc: "VIEW-ONLY. Check for conflicts after submitting attendance in Group Attendance. If you see a red conflict, notify your supervisor." },
                { icon: ClipboardCheck, name: "Group Attendance", desc: "Where you actually submit daily attendance. Opens the external Group Attendance app — sign in with Group Code + Name. Submit by 10:00 AM." },
                { icon: Bell, name: "Alerts", desc: "Early dismissal and late arrival notifications from the front desk. Shows notification badge count." },
                { icon: Utensils, name: "Dining", desc: "Lunch ordering — select each camper's meal choice. Shows allergy and dietary flags." },
                { icon: Bus, name: "Bus Info", desc: "View each camper's AM and PM bus assignments." },
                { icon: Heart, name: "Health Notes", desc: "Quick view of medical conditions, allergies, and health alerts for your group." },
                { icon: Gamepad2, name: "Hobbies", desc: "View camper hobby selections for each week (Mariners & Navigators only)." },
                { icon: Gamepad2, name: "Intensives", desc: "Navigator-only multi-week intensive activity assignments." },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border">
                  <c.icon className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                  <div><span className="font-bold text-gray-800 text-sm">{c.name}</span><span className="text-sm text-gray-600"> — {c.desc}</span></div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 italic">Note: Some cards only appear for certain groups. Mariner groups see Intramurals team/schedule/playoff cards. Navigator groups see Navigator Intramurals.</p>
          </div>

          {/* ═══ 3. DAILY ROSTER ═══ */}
          <H id="daily-roster" num="3" title="Daily Roster" icon={Users} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Daily Roster shows every camper <strong>scheduled to be at camp today</strong>. It automatically filters based on each camper's enrollment:</p>
            <ul className="list-disc pl-6 space-y-1 text-base text-gray-700">
              <li><strong>Full Season</strong> — here every day of camp</li>
              <li><strong>HS1 (Half Season 1)</strong> — first half of summer</li>
              <li><strong>HS2 (Half Season 2)</strong> — second half of summer</li>
              <li><strong>6-Week</strong> — during their registered 6-week block</li>
              <li><strong>Flex 20/25/30</strong> — only on their specific pre-selected days</li>
            </ul>
            <p className="text-base text-gray-700 font-medium mt-3">What you'll see on each camper card in Daily Roster:</p>
            <div className="space-y-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl"><span className="font-bold text-sm text-amber-800">Lunch Order</span><span className="text-sm text-amber-700"> — today's submitted lunch choice (or "No lunch order" if missing).</span></div>
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-xl"><span className="font-bold text-sm text-cyan-800">Swim Lesson</span><span className="text-sm text-cyan-700"> — <strong>only appears on days the camper has a lesson</strong>, with the time shown.</span></div>
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl"><span className="font-bold text-sm text-sky-800">Swim Color</span><span className="text-sm text-sky-700"> — the wristband color + label (used for pool buddy pairing).</span></div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl"><span className="font-bold text-sm text-green-800">Bus Info</span><span className="text-sm text-green-700"> — AM and PM bus assignments.</span></div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl"><span className="font-bold text-sm text-blue-800">Color War Team</span><span className="text-sm text-blue-700"> — Blue or White badge (only when admin reveals Color War).</span></div>
              <div className="p-3 bg-orange-50 border border-orange-300 rounded-xl"><span className="font-bold text-sm text-orange-800">Early Dismissal</span><span className="text-sm text-orange-700"> — orange pill with the pickup time if the front desk flagged the camper for early pickup. A persistent <strong>amber banner</strong> stays on the camper card all day until pickup.</span></div>
              <div className="p-3 bg-sky-50 border border-sky-300 rounded-xl"><span className="font-bold text-sm text-sky-800">Late Arrival</span><span className="text-sm text-sky-700"> — blue pill with the arrival time if the camper is arriving late. A persistent <strong>amber banner</strong> stays on the camper card until they're marked in.</span></div>
              <div className="p-3 bg-red-50 border border-red-300 rounded-xl"><span className="font-bold text-sm text-red-800">AM / PM Exception</span><span className="text-sm text-red-700"> — if Admin set a dismissal exception for this camper today (e.g. different pickup location), a <strong>red box overlay</strong> appears on the camper card showing the exception note. The camper is also pulled from the AM or PM bus roster for that day.</span></div>
            </div>
            <Steps items={[
              "Tap the Daily Roster card from your home screen.",
              "You'll see every camper scheduled for today.",
              "Each card shows lunch, swim lesson (if applicable that day), swim color, bus, Color War team, and any early-dismissal or late-arrival pill.",
              "Tap any camper card to open their full Camper Card (medical, allergies, parents, pickup authorization).",
              "Use the date picker at the top to view rosters for other days.",
              "Flex campers auto-appear/disappear based on their pre-selected schedule — always trust the roster.",
            ]} />
            <Img caption="Daily Roster — shows who's at camp today with lunch, swim, Color War, and early-dismissal indicators." src={IMG.roster} />
            <Why>The Daily Roster is the single daily source of truth for who is at camp and what they have going on. Check lunch and early-dismissal status before lunch and before 2:30 PM pickup.</Why>
            <Mistakes items={[
              "Assuming any camper will be there without checking the roster — always confirm",
              "Missing an orange early-dismissal pill — always scan for them after lunch",
              "Marking a camper absent when they're actually not enrolled for that day",
            ]} />
          </div>

          {/* ═══ 4. CAMPER CARD ═══ */}
          <H id="camper-card" num="4" title="The Camper Card" icon={Users} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Tap any camper's name (from the Campers card or Daily Roster) to see their <strong>full profile card</strong>. This contains everything you need to know about a camper:</p>
            <Img caption="Camper card — tap any name to see full details including medical, parent, and swim info" src={IMG.campers} />
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 rounded-xl border">
                <p className="font-bold text-sm text-gray-800">Personal Info</p>
                <p className="text-sm text-gray-600">Full name, preferred name (nickname), group code, age, grade, session type.</p>
              </div>
              <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                <p className="font-bold text-sm text-red-800">Medical & Allergies</p>
                <p className="text-sm text-red-700">All medical conditions, allergies (food, environmental, medication), medications, and special health notes. <strong>Review this before the first day of each session.</strong></p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="font-bold text-sm text-blue-800">Swim Level & Color</p>
                <p className="text-sm text-blue-700">Swim assessment color (wristband color). This determines which pool areas the camper can access and who they can buddy with. <strong>Same swim color = can be buddies at the pool.</strong></p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                <p className="font-bold text-sm text-green-800">Parent / Guardian Info</p>
                <p className="text-sm text-green-700">Parent names, phone numbers, email addresses. This is who receives your messages when you use the Messages feature.</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="font-bold text-sm text-amber-800">Bus Information</p>
                <p className="text-sm text-amber-700">AM bus (morning arrival) and PM bus (afternoon departure) assignments. Shows bus name/number.</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                <p className="font-bold text-sm text-purple-800">Color War Team</p>
                <p className="text-sm text-purple-700">Blue or White team assignment (only visible when the admin reveals Color War).</p>
              </div>
            </div>
            <Why>Knowing a camper's allergies, swim level, and medical needs BEFORE an issue arises prevents emergencies. Review camper cards at the start of each session.</Why>
          </div>

          {/* ═══ 5. GROUP ATTENDANCE (FIRST) ═══ */}
          <H id="group-attendance" num="5" title="Group Attendance (do this FIRST)" deadline={true} icon={ClipboardCheck} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700"><strong>This is where daily attendance is submitted.</strong> Do this FIRST in the morning, then check Conflict Match (Section 6).</p>

            <Steps items={[
              "Tap the Group Attendance card on your home screen.",
              "Sign in with your Group Code and group name (same as Camper Snapshot).",
              "Mark each camper Present or Absent. (Late = front desk, not you.)",
              "Submit before 10:00 AM.",
              "Open Conflict Match next.",
            ]} />

            <Mistakes items={[
              "Trying to do attendance inside Conflict Match — wrong place. Use Group Attendance.",
              "Marking a camper Late — you don't. Front desk does.",
              "Skipping Conflict Match after submitting.",
              "Missing the 10:00 AM deadline.",
            ]} />
            <p className="text-sm text-sky-900 mt-2">Full step-by-step: <a href="/tutorialattendance" className="text-sky-700 underline font-semibold">Attendance App Tutorial</a> · <a href="/tutorialbus" className="text-sky-700 underline font-semibold">Bus App Tutorial</a></p>
          </div>

          {/* ═══ 6. ATTENDANCE SYNC (AFTER) ═══ */}
          <H id="attendance" num="6" title="Conflict Match — check AFTER Group Attendance" deadline={true} icon={ClipboardCheck} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700"><strong>View-only.</strong> Compares your Group Attendance submission against bus check-ins to surface any mismatches.</p>

            <Steps items={[
              "After Group Attendance, tap Conflict Match.",
              "All green rows = done.",
              "RED row = conflict. Notify your supervisor — do not resolve yourself. Your supervisor is also auto-texted (SMS) the moment a conflict is detected.",
              "AMBER row with clock = front desk marked the camper Late. No action needed.",
              "Tap the printer icon for today's report.",
            ]} />

            <Img caption="Conflict Match — view-only. Red rows = conflicts (notify supervisor). Amber = late (handled)." src={IMG.attendance} />
            <Mistakes items={[
              "Skipping this step after Group Attendance",
              "Trying to resolve a red conflict yourself",
              "Thinking attendance is done here — it isn't, this is view-only",
            ]} />
          </div>

          {/* ═══ 6. BUDDY SHEET (POOL ONLY) ═══ */}
          <H id="buddy-sheet" num="7" title="Buddy Check Sheet (Pool Only)" icon={Waves} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700"><strong>Pool only.</strong> Not a morning check-in. Pair every camper with a swim buddy of the <strong>same swim color</strong>.</p>
            <Img caption="Buddy Sheet — toggle AM / PM, drag & drop same-color pairs, save each period." src={IMG.buddy} />
            <Steps items={[
              "AM/PM toggle at the top — they save separately.",
              "Drag campers into the buddy slots. Same swim color only.",
              "Tap Save. Use Dup → PM if AM/PM are identical.",
              "Odd number? Make a trio. Never solo.",
              "At the pool: call \"Buddy Check!\", count pairs, act on any solo hand.",
            ]} />
            <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
              <p className="text-sm text-red-900"><strong>Admin lock:</strong> Admin can manually <strong>lock</strong> the Buddy Sheet (e.g. after the 9:20 AM deadline). When locked, the Save button is disabled and you cannot change pairs — only Admin can unlock it. If you missed the deadline, find a supervisor.</p>
            </div>
            <Why>Buddy checks are camp's primary pool headcount. A missed buddy check is a safety incident.</Why>
            <Mistakes items={[
              "Pairing campers with different swim colors",
              "Saving AM and assuming PM is also saved — they are separate",
              "Leaving a camper without a buddy",
            ]} />
          </div>

          {/* ═══ 8. LUNCH ORDERING (DINING) ═══ */}
          <H id="dining" num="8" title="Lunch Ordering" deadline={true} icon={Utensils} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Lunch orders go through the <strong>Dining app</strong>. Submit by 9:30 AM — kitchen starts prep right after.</p>

            <Steps items={[
              "Tap the Dining card → opens the Dining app.",
              "Log in with your group number.",
              "Pick each camper's lunch from the drop-down.",
              "HIT SAVE on each camper, or use \"Save All\" at the bottom.",
              "Submit before 9:30 AM.",
              "Trip lunches: order up to a week in advance from the same screen.",
            ]} />

            <div className="p-3 bg-red-50 border-2 border-red-500 rounded-xl">
              <p className="font-bold text-red-900 text-sm">A selection without a save does not count.</p>
            </div>

            <Mistakes items={[
              "Picking a lunch and forgetting to Save",
              "Submitting after 9:30 AM",
              "Waiting until the morning of a trip — order trip lunches the week before",
              "Overlooking allergy flags",
            ]} />
            <p className="text-sm text-sky-900 mt-2">Full step-by-step: <a href="/tutorialdining" className="text-sky-700 underline font-semibold">Lunch App Tutorial</a></p>
          </div>

          {/* ═══ 8. ALERTS ═══ */}
          <H id="alerts" num="9" title="Alerts — Early Dismissals & Late Arrivals" icon={Bell} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">When the front desk creates an early dismissal or late arrival, you see it in two places:</p>
            <ul className="list-disc pl-6 space-y-1 text-base text-gray-700">
              <li><strong>Banner</strong> at the top of your dashboard — orange (early dismissal) or blue (late arrival). Tap X to dismiss after acting.</li>
              <li><strong>Alerts card</strong> with a red badge count — shows today's active and cleared alerts.</li>
            </ul>
            <Img caption="Alerts view — active alerts at top, cleared below." src={IMG.alerts} />

            <p className="text-base text-gray-700"><strong>The front desk releases the camper, not you.</strong> When you see an early dismissal alert, walk the camper to the front desk at pickup time.</p>

            <Steps items={[
              "Watch for orange/blue banners and the badge on the Alerts card.",
              "Early Dismissal: get camper's belongings, walk to front desk at pickup time.",
              "Late Arrival: welcome the camper when they arrive.",
              "Tap X to dismiss once handled.",
              "Alerts archive automatically each day.",
            ]} />
          </div>

          {/* ═══ 9. MESSAGES ═══ */}
          <H id="messages" num="10" title="Messages" icon={MessageSquare} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Text or email parents. SMS is the default. Each camper has its own conversation thread.</p>
            <Img caption="Messages — group inbox: one card per camper family." src={IMG.messages} />
            <Steps items={[
              "Tap Messages on your home screen.",
              "Tap a camper card to open the conversation.",
              "Type at the bottom, pick SMS or Email, tap Send.",
              "Use the date picker to find messages from a specific day.",
              "Tap Message All to text every family in the group.",
              "Parent replies come back into the same thread automatically.",
            ]} />
            <Why>Use messaging for non-urgent parent updates. For emergencies, find your supervisor in person — never use the app.</Why>
            <Mistakes items={["Using messaging for emergencies instead of finding your supervisor in person", "Sending to the wrong camper — verify the name on the thread", "Not checking for parent replies", "Sending unprofessional messages — everything is recorded"]} />
          </div>

          {/* ═══ 10. INCIDENT REPORTS ═══ */}
          <H id="incident-reports" num="11" title="Incident Reports" icon={AlertTriangle} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">File an incident report for ANY injury, behavioral issue, conflict, or safety concern. Your supervisor is automatically notified when you submit.</p>
            <Img caption="Incident Report — select camper, choose type, describe details, submit" src={IMG.incidents} />
            <Steps items={[
              "Tap \"Incident Report\" from your home screen.",
              "Select the camper(s) involved.",
              "Choose the incident type (injury, behavioral, conflict, other).",
              "Describe what happened in detail — who, what, when, where, and what action you took.",
              "Include any first aid given, whether a parent was called, or if the camper went to the health office.",
              "Submit immediately — do not wait until end of day.",
              "If the incident is SERIOUS (head injury, allergic reaction, missing camper) — find your supervisor in person first, then file the report.",
            ]} />
            <Why>Documentation protects the camper, you, and the camp. Late or incomplete reports create liability gaps. File every incident immediately.</Why>
            <Mistakes items={["Waiting until end of day to file", "Being vague — include specific details", "Logging a serious incident in the app and walking away — find your supervisor in person first"]} />
          </div>

          {/* ═══ 11. EVALUATIONS ═══ */}
          <H id="evaluations" num="12" title="End-of-Season Evaluations" icon={Star} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">At the end of each session, you write evaluations for each camper in your group. These are shared with parents and stored for next year's counselors.</p>
            <Steps items={[
              "Tap \"Evaluations\" from your home screen.",
              "Select a camper to evaluate.",
              "Fill in the evaluation form — strengths, areas for growth, social interactions, memorable moments.",
              "The system offers an AI summary suggestion based on your notes. You can use it as-is, edit it, or write your own.",
              "Submit when done. Your supervisor can review evaluations before they go to parents.",
            ]} />
          </div>

          {/* ═══ 12. TRIP FORM ═══ */}
          <H id="trip-form" num="13" title="Trip Accountability Form" icon={MapPin} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Used for field trips and off-site activities. The Trip Form is a headcount sheet — you check off each camper present on the trip, at each checkpoint.</p>
            <Steps items={[
              "Tap \"Trip Form\" from your home screen.",
              "Select the trip/event.",
              "Check off each camper as they board the bus or arrive at the activity.",
              "Do a recount at each stop — departure, arrival, return.",
              "Submit the form when the trip is complete.",
            ]} />
          </div>

          {/* ═══ 14. GROUP SCHEDULE ═══ */}
          <H id="group-schedule" num="14" title="Group Schedule" icon={CalendarDays} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Your full week in 10-minute blocks. If admin has uploaded a schedule for your group, the <strong>Group Schedule</strong> card appears on your home screen.</p>
            <Steps items={[
              "Tap \"Group Schedule\" on your home screen.",
              "You'll see a grid: Time down the left, Monday–Friday across the top. Back-to-back same activities are merged into one colored block.",
              "Use the three tabs at the top to switch variants:",
            ]} />
            <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Badge className="bg-sky-100 text-sky-800 border-sky-300 mt-0.5">Regular</Badge>
                <p className="text-sm text-sky-900">Your normal weekly schedule. Default view.</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 mt-0.5">Special Event</Badge>
                <p className="text-sm text-fuchsia-900">Fridays, Color War, and the final week. Admin updates this whenever it applies.</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-slate-200 text-slate-800 border-slate-300 mt-0.5">Rainy Day</Badge>
                <p className="text-sm text-slate-900">Indoor-only backup. Use it whenever the camp calls a rain day.</p>
              </div>
            </div>
            <p className="text-sm text-gray-700">Greyed-out tabs mean that variant hasn't been uploaded yet — stick with Regular.</p>
          </div>

          {/* ═══ 15. HEALTH NOTES ═══ */}
          <H id="health-notes" num="15" title="Health Notes" icon={Heart} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Health Notes card gives you a quick view of all medical conditions, allergies, and health alerts across your entire group — without having to open each camper individually.</p>
            <Steps items={[
              "Tap \"Health Notes\" from your home screen.",
              "You'll see a summary of campers with medical conditions, allergies, or special health needs.",
              "Review this at the start of each session, especially when new campers join your group.",
              "If you notice a health issue during the day, check the camper's full card for details before taking action.",
            ]} />
          </div>

          {/* ═══ 16. BUS INFO ═══ */}
          <H id="bus-info" num="16" title="Bus Information" icon={Bus} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Each camper's AM bus (morning) and PM bus (afternoon) assignment is shown on their camper card and in the Bus Info view. This data syncs from the Bus Mapping App.</p>
            <Steps items={[
              "View bus assignments on individual camper cards (look for the bus badge).",
              "Or use the daily roster — bus info is visible on each camper row.",
              "At the end of the day, confirm each camper is going to the correct PM bus before dismissing them.",
            ]} />
          </div>

          {/* ═══ 17. HOBBIES, INTRAMURALS & INTENSIVES ═══ */}
          <H id="hobbies" num="17" title="Hobbies, Intramurals & Intensives" icon={Gamepad2} />
          <div className="mt-4 space-y-4">
            <p className="text-base text-gray-700">Programming varies by division. Find your group below for the exact rules — then scroll down for step-by-step instructions on how to use each selection form.</p>

            <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-4">
              <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2"><Trophy className="w-4 h-4" /> 3rd & 4th Grade Mariners (Boys & Girls)</h4>
              <p className="text-sm text-indigo-800 mb-2">Your campers have <strong>Hobbies</strong> (weekly activity choices) and <strong>Intramurals</strong> (team sports competition).</p>
              <p className="text-sm text-indigo-700 mb-2"><strong>Hobbies:</strong> Selection window opens Monday 12:00 AM and closes <strong>Tuesday 2:00 PM</strong>. After that it's locked for the week.</p>
              <p className="text-sm text-indigo-700 mb-2"><strong>Intramurals:</strong> Teams are assigned by your athletics director — <strong>you do not pick</strong>. View the schedule, standings, and playoff brackets from the Intramurals card on your home screen. Scoring: <strong>Win = 10 pts, Tie = 5 pts, Loss = 3 pts.</strong></p>
            </div>

            <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4">
              <h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2"><Gamepad2 className="w-4 h-4" /> Navigators (5th–8th Grade)</h4>
              <p className="text-sm text-purple-800 mb-2">Your campers have <strong>Hobbies</strong>, <strong>Intramurals</strong>, AND <strong>Intensives</strong>.</p>
              <p className="text-sm text-purple-700 mb-2"><strong>Hobbies:</strong> Selection window opens Monday 9:00 AM and closes <strong>Thursday 2:00 PM</strong>. Campers can change their selection until the window closes.</p>
              <p className="text-sm text-purple-700 mb-2"><strong>Intramurals:</strong> 10 co-ed teams with a 6-week round robin, standings, and playoffs. Athletics director assigns the teams — you don't pick.</p>
              <p className="text-sm text-purple-700"><strong>Intensives:</strong> Multi-week deep-dive. Selection window opens Monday 9:00 AM and closes <strong>Tuesday 2:00 PM</strong>. <strong>Week 1 selection LOCKS the camper into that intensive for all 6 weeks.</strong> No changes after that.</p>
            </div>

            <div className="bg-gray-100 border border-gray-300 rounded-xl p-4">
              <h4 className="font-bold text-gray-700 mb-2">Skippers (Nursery–K) & Younger Mariners (B01–B07, G01–G07)</h4>
              <p className="text-sm text-gray-600">Hobbies, Intramurals, and Intensives <strong>do not apply to your group</strong>. Your daily programming follows your division's printed schedule.</p>
            </div>

            {/* ── How to use the selection form ── */}
            <div className="bg-white border-2 border-violet-300 rounded-xl p-4 mt-4">
              <h4 className="font-bold text-violet-900 mb-3 flex items-center gap-2"><Gamepad2 className="w-4 h-4" /> How to Use the Hobbies / Intensives Selection Form</h4>
              <p className="text-sm text-gray-700 mb-3"><strong>YOU pick for each camper — parents and campers do not pick on their own.</strong> The form lives inside Camper Snapshot.</p>
              <Steps items={[
                <span key="1">From your home screen, tap the <strong>Hobbies</strong> card (or <strong>Intensives</strong> card, for Navigators in week 1).</span>,
                <span key="2">The top of the page shows a row of <strong>week pills</strong>. A <strong className="text-green-700">green pill</strong> means the window is open for that week. Gray = past. Violet = future/locked.</span>,
                <span key="3">If the window is <strong>closed</strong>, you'll see a lock icon. You cannot make or change selections — contact <strong>Brian Stein</strong> if you have a late camper.</span>,
                <span key="4">If the window is <strong>open</strong>, you'll see your roster for the target day of that week.</span>,
                <span key="5">For <strong>each camper</strong>: tap the camper's name, then tap the activity they want from the list. The selection saves immediately — you'll see a green checkmark.</span>,
                <span key="6"><strong>Mariners:</strong> Activities cannot be repeated within the same week. If a camper already picked "Art" on Wednesday, "Art" will be grayed out for the rest of that week.</span>,
                <span key="7"><strong>Navigators:</strong> Same activity can be picked across weeks but each day of the week needs a different selection.</span>,
                <span key="8"><strong>Intensives (Navigators only):</strong> You pick <strong>ONCE in Week 1</strong>. That selection locks the camper into the same intensive for all 6 weeks. Pick carefully — changes require Brian Stein.</span>,
                <span key="9">To change a selection before the window closes, tap the camper again and tap a different activity. The old pick is overwritten.</span>,
                <span key="10">After the window closes, the <strong>Hobbies / Intensives card on your home screen</strong> shows each camper's final assignment. Use it every morning to get the right camper to the right activity.</span>,
              ]} />
              <div className="mt-3 bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg">
                <p className="text-sm text-amber-900"><strong>If you miss the selection window:</strong> The camper will be auto-placed in a default activity by the system. To avoid this — lock in selections by the deadline every week.</p>
              </div>
            </div>

            {/* ── Intramurals (read-only for counselors) ── */}
            <div className="bg-white border-2 border-orange-300 rounded-xl p-4">
              <h4 className="font-bold text-orange-900 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4" /> How to View Intramurals</h4>
              <p className="text-sm text-gray-700 mb-3"><strong>You do NOT pick intramural teams.</strong> The athletics director assigns every camper. Your job is to know who's on what team and get them to games on time.</p>
              <Steps items={[
                <span key="1">From your home screen, tap the <strong>Intramurals</strong> card for your division.</span>,
                <span key="2">The top of the page shows <strong>team standings</strong> — tap any team to see its roster (your campers will have their badges shown).</span>,
                <span key="3">Tap the <strong>Schedule</strong> tab to see game times, fields, and matchups for the current week.</span>,
                <span key="4">Tap the <strong>Playoffs</strong> tab (weeks 5–6) to see the bracket.</span>,
                <span key="5">On game day: check the schedule the night before so you know where your team needs to be and at what time.</span>,
              ]} />
            </div>
          </div>

          {/* ═══ 16. COLOR WAR ═══ */}
          <H id="color-war" num="18" title="Color War" icon={Flag} />
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Color War is the camp-wide Blue vs White team competition. Every camper and staff member is assigned to either Blue or White.</p>
            <Steps items={[
              "Color War teams are assigned by the admin. You will NOT see team assignments until the admin reveals them.",
              "Once revealed, each camper's team (Blue or White) appears as a badge on their camper card and on the daily roster.",
              "The admin can hide teams again at any time (before the big reveal).",
              "If you need to check a camper's team, open their camper card.",
            ]} />
            <p className="text-sm text-gray-500 italic">Sibling separation: siblings (same last name) are automatically placed on opposite teams, except for Skippers.</p>
          </div>

          {/* ═══ 17. TROUBLESHOOTING ═══ */}
          <H id="troubleshooting" num="19" title="Troubleshooting" icon={HelpCircle} />
          <div className="mt-4 space-y-3">
            {[
              { q: "App won't load", a: "Check internet. Try refreshing. If on camp Wi-Fi, switch to cellular data." },
              { q: "Camper missing from roster", a: "They may not be enrolled for today (Flex or HS transition). Check with your supervisor." },
              { q: "Wrong session type showing", a: "Session data comes from CampMinder. Tell your supervisor — they can trigger a data re-sync." },
              { q: "Can't send messages", a: "Check if messaging is enabled (supervisor controls this toggle). Make sure you selected at least one recipient." },
              { q: "Notifications not appearing", a: "Alerts refresh every 15 seconds. Try pulling down to refresh. Check the Alerts card for active notifications." },
              { q: "Red conflict row on Conflict Match", a: "Notify your supervisor immediately. Do NOT try to resolve it yourself — only supervisors resolve conflicts." },
              { q: "Buddy sheet won't let me pair two campers", a: "They have different swim colors. You can only pair campers with the same swim wristband color." },
              { q: "Nothing works", a: "Contact Brian Stein." },
            ].map((item, i) => (
              <div key={i} className="border rounded-lg p-3">
                <p className="font-bold text-sm text-gray-800">{item.q}</p>
                <p className="text-sm text-gray-600 mt-1">{item.a}</p>
              </div>
            ))}
          </div>

          {/* Footer */}
          <footer className="mt-12 pt-6 border-t text-center space-y-3 pb-8">
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 inline-block">
              <p className="text-sm font-bold text-sky-900">Tech Support: Brian Stein</p>
            </div>
            <p className="text-xs text-gray-400">Rolling River Day Camp — Counselor Tutorial — Last updated April 2026</p>
          </footer>
        </main>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 bg-sky-700 text-white p-3 rounded-full shadow-lg hover:bg-sky-600 z-50 print:hidden"><ChevronUp className="w-5 h-5" /></button>
      )}
    </div>
  );
}
